import AssignmentFeedback from 'models/assignments/AssignmentFeedback';

/**
 * Formats text with feedback highlights, showing only active highlight
 *
 * This function:
 * 1. Takes text content and an active feedback ID
 * 2. Creates spans around text segments that have feedback attached
 * 3. Only displays highlights for the active feedback item
 * 4. Preserves existing formatting while adding highlights
 *
 * @param text The original text content
 * @param activeFeedbackId ID of the currently active feedback, or null if none
 * @param feedback Array of all feedback items
 * @returns Formatted HTML string with highlights applied
 */
export function formatTextWithHighlights(
  text: string,
  activeFeedbackId: string | null,
  feedback: AssignmentFeedback[],
): string {
  if (!text) return '';

  const textLength = text.length;

  // Get only the annotations with valid start/end indices
  const inlineAnnotations = feedback.filter(
    (item) => item.start_index !== undefined && item.end_index !== undefined,
  );

  // Create arrays to track annotation openings and closings
  const annotationStarts: { index: number; feedback: AssignmentFeedback }[] =
    [];
  const annotationEnds: { index: number; feedback: AssignmentFeedback }[] = [];

  // Populate the arrays with all valid inline annotations
  inlineAnnotations.forEach((feedback) => {
    if (feedback.start_index === undefined || feedback.end_index === undefined)
      return;

    // Ensure indices are within bounds
    const startIndex = Math.max(0, Math.min(feedback.start_index, textLength));
    const endIndex = Math.max(
      startIndex,
      Math.min(feedback.end_index, textLength),
    );

    annotationStarts.push({ index: startIndex, feedback });
    annotationEnds.push({ index: endIndex, feedback });
  });

  // Sort the arrays by index
  annotationStarts.sort((a, b) => a.index - b.index);
  annotationEnds.sort((a, b) => a.index - b.index);

  // Create an array of text segments with their annotation states
  const segments: { text: string; activeAnnotations: AssignmentFeedback[] }[] =
    [];
  let currentIndex = 0;
  let activeAnnotations: AssignmentFeedback[] = [];

  // Process all events (annotation starts and ends) in order
  const allEvents = [
    ...annotationStarts.map((item) => ({
      index: item.index,
      isStart: true,
      feedback: item.feedback,
    })),
    ...annotationEnds.map((item) => ({
      index: item.index,
      isStart: false,
      feedback: item.feedback,
    })),
  ].sort((a, b) => a.index - b.index || (a.isStart ? -1 : 1));

  // Handle each event
  allEvents.forEach((event) => {
    if (event.index > currentIndex) {
      // Add segment before this event
      segments.push({
        text: text.substring(currentIndex, event.index),
        activeAnnotations: [...activeAnnotations],
      });
      currentIndex = event.index;
    }

    // Update active annotations list
    if (event.isStart) {
      activeAnnotations.push(event.feedback);
    } else {
      activeAnnotations = activeAnnotations.filter(
        (a) => a.id !== event.feedback.id,
      );
    }
  });

  // Add final segment if needed
  if (currentIndex < textLength) {
    segments.push({
      text: text.substring(currentIndex),
      activeAnnotations: [],
    });
  }

  // Convert segments to HTML
  let formattedHtml = '';

  segments.forEach((segment) => {
    if (segment.text.length === 0) return;

    let segmentHtml = segment.text;

    // Apply spans for all active annotations, from innermost to outermost
    segment.activeAnnotations.forEach((annotation) => {
      const isActive = annotation.id === activeFeedbackId;
      segmentHtml = `<span class="highlighted-text ${
        annotation.correction_type
      } ${isActive ? 'active' : 'inactive'}"
        data-feedback-id="${annotation.id}"
        title="${annotation.text}">${segmentHtml}</span>`;
    });

    formattedHtml += segmentHtml;
  });

  // Wrap everything in paragraph tags if not already formatted
  if (!formattedHtml.includes('<p>')) {
    formattedHtml = `<p>${formattedHtml}</p>`;
  }

  return formattedHtml;
}
